import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { ContentBlock } from "@/components/wpl/content-block/src";
import { DEFAULT_VALUES } from "@/libraries/web-framework";
import { Icon } from "@/libraries/wpl/foundations/src";

const sectionStyles = css({
    "backgroundColor": "#ffffff",
    "padding": `${token("space.400")} 0`,

    [media.above.sm]: {
        paddingBlockEnd: token("space.800")
    },

    "h2": {
        fontWeight: "500",
        textAlign: "center",
        color: "#172b4d",
        padding: `${token("space.400")} ${token("space.200")} ${token(
            "space.200"
        )} `,

        [media.below.sm]: {
            fontSize: "1.5rem",
            margin: "0 0 14px"
        },
        [media.above.sm]: {
            fontSize: "2.25rem",
            margin: "0 0 24px"
        }
    },

    "h3": {
        fontSize: "2rem",
        color: "#253858",
        margin: "0 0 16px"
    },

    ".text-section": {
        h3: {
            [media.below.sm]: {
                color: "#253858",
                fontSize: "1.25rem",
                margin: "0 0 14px"
            },
            [media.above.sm]: {
                color: "#253858",
                fontSize: "2rem",
                margin: "0 0 24px" // value obtained through manual visual comparison agaisnt prod
            }
        },

        p: {
            margin: "0"
        }
    },

    ".contentBlock-body": {
        color: "#253858"
    }
});

const outerGridStyles = css({
    maxWidth: "1200px",
    width: "100%",
    margin: "auto",
    display: "grid",

    [media.below.md]: {
        gridTemplateColumns: "1fr"
    },
    [media.above.md]: {
        gridTemplateColumns: "1fr 1fr"
    }
});

const innerGridStyles = css({
    display: "grid",

    [media.below.sm]: {
        gridTemplateColumns: "1fr"
    },
    [media.above.sm]: {
        gridTemplateColumns: "1fr 1fr"
    }
});

const columnStyles = css({
    display: "block",
    padding: `${token("space.200")} ${token("space.200")} ${token(
        "space.400"
    )} `
});

const iconStyles = css({
    borderRadius: "50%",
    backgroundColor: "#deebff",
    width: "60px",
    height: "60px",
    marginBlockEnd: token("space.200")
});

const innerIconStyles = css({
    margin: "auto",
    paddingBlockStart: "14px",
    width: token("space.400"),
    height: token("space.400")
});

const WhyConfluenceSection = () => {
    const intl = useIntl();

    const sectionContent = {
        organizationTitle: intl.formatMessage({
            id: "ConfluenceSoftwarePageTemplate.whyConfluenceSection.organizationTitle",
            defaultMessage: "Organization that lasts"
        }),
        organizationBody: intl.formatMessage({
            id: "ConfluenceSoftwarePageTemplate.whyConfluenceSection.organizationBody",
            defaultMessage:
                "As your company grows, so does complexity. Stay organized along the way with tools that keep content current and discoverable."
        }),
        everyoneTitle: intl.formatMessage({
            id: "ConfluenceSoftwarePageTemplate.whyConfluenceSection.everyoneTitle",
            defaultMessage: "Get everyone on the same page"
        }),
        everyoneBody: intl.formatMessage({
            id: "ConfluenceSoftwarePageTemplate.whyConfluenceSection.everyoneBody",
            defaultMessage:
                "Create a single source of truth. Teams stay aligned without requesting access or searching across different tools."
        }),
        ideasTitle: intl.formatMessage({
            id: "ConfluenceSoftwarePageTemplate.whyConfluenceSection.ideasTitle",
            defaultMessage: "Where ideas come to life"
        }),
        ideasBody: intl.formatMessage({
            id: "ConfluenceSoftwarePageTemplate.whyConfluenceSection.ideasBody",
            defaultMessage:
                "Move from brainstorm to breakthrough. Connect project plans to action items by integrating your favorite project management apps."
        }),
        workspacesTitle: intl.formatMessage({
            id: "ConfluenceSoftwarePageTemplate.whyConfluenceSection.workspacesTitle",
            defaultMessage: "Flexible workspaces"
        }),
        workspacesBody: intl.formatMessage({
            id: "ConfluenceSoftwarePageTemplate.whyConfluenceSection.workspacesBody",
            defaultMessage:
                "More than just documents and spreadsheets. Create plans with text, tables, images, timelines, code, and more - all in one place."
        })
    };

    return (
        <section css={sectionStyles}>
            <h2>
                <FormattedMessage
                    id="ConfluenceSoftwarePageTemplate.whyConfluenceSection.header"
                    defaultMessage="Why Confluence?"
                    values={DEFAULT_VALUES}
                />
            </h2>

            <div css={outerGridStyles}>
                <div css={innerGridStyles}>
                    <div css={columnStyles}>
                        <div css={iconStyles}>
                            <div css={innerIconStyles}>
                                <Icon
                                    name="building"
                                    size="small"
                                    color="n800"
                                />
                            </div>
                        </div>
                        <ContentBlock
                            headerType="h3"
                            headerText={sectionContent.organizationTitle}
                            bodyText={sectionContent.organizationBody}
                        />
                    </div>
                    <div css={columnStyles}>
                        <div css={iconStyles}>
                            <div css={innerIconStyles}>
                                <Icon
                                    name="checkmark"
                                    size="small"
                                    color="n800"
                                />
                            </div>
                        </div>
                        <ContentBlock
                            headerType="h3"
                            headerText={sectionContent.everyoneTitle}
                            bodyText={sectionContent.everyoneBody}
                        />
                    </div>
                </div>
                <div css={innerGridStyles}>
                    <div css={columnStyles}>
                        <div css={iconStyles}>
                            <div css={innerIconStyles}>
                                <Icon
                                    name="locationpin"
                                    size="small"
                                    color="n800"
                                />
                            </div>
                        </div>
                        <ContentBlock
                            headerType="h3"
                            headerText={sectionContent.ideasTitle}
                            bodyText={sectionContent.ideasBody}
                        />
                    </div>
                    <div css={columnStyles}>
                        <div css={iconStyles}>
                            <div css={innerIconStyles}>
                                <Icon
                                    name="maximize"
                                    size="small"
                                    color="n800"
                                />
                            </div>
                        </div>
                        <ContentBlock
                            headerType="h3"
                            headerText={sectionContent.workspacesTitle}
                            bodyText={sectionContent.workspacesBody}
                        />
                    </div>
                </div>
            </div>
        </section>
    );
};

export default WhyConfluenceSection;
